<template>
    <layout-page :nav-bar-show-logo-text="false">
        <page-breadcrumb :hierarchy="breadCrumbs"/>
        <div class="container">
            <h1 class="font-large text-blue-60 text-2xl text-center py-4 lg:text-left lg:text-3xl">
                <span class="font-extrabold">Privacy Policy</span>
            </h1>
            <ol class="list-decimal text-lg text-grey-70">
                <li>
                    <div id="general-information">
                        <span class="font-bold">General Information</span>
                        <p class="pt-2 pb-2">
                            This privacy notice sets out the ways in which Agency Central Limited, company number 03956696, (Agency Central) collects, processes and shares your personal data.
                        </p>
                    </div>
                </li>

                <li>
                    <div id="what-information-does-agency-central-collect">
                        <span class="font-bold">What information does Agency Central collect?</span>
                        <p class="pt-2 pb-2">
                            Agency Central may collect, process and use the following information that you may provide when you use or sign up to receive our services:
                        </p>
                        <ul class="list-inside list-disc ml-5">
                            <li>name</li>
                            <li>email address</li>
                            <li>telephone number</li>
                            <li>enquiry, chat and email content</li>
                            <li>fax numbers</li>
                            <li>data contained in your CV</li>
                            <li>social media information</li>
                            <li>any other information you provide to us.</li>
                        </ul>

                        <p class="pt-2 pb-2">Agency Central may collect, process and use the following information about you when you use our website:</p>

                        <ul class="list-inside list-disc ml-5">
                            <li>your IP address</li>
                            <li>pages visited</li>
                            <li>links clicked on</li>
                            <li>visitor numbers</li>
                        </ul>
                    </div>
                </li>


                <li class="text-lg text-grey-70 font-weight-bold">
                    <div id="what-is-your-data-used-for">
                        <span class="font-bold">What is your data used for?</span>
                        <p class="pt-2 pb-2">
                            Agency Central processes your personal data in the following ways, and on the following legal bases:
                        </p>

                        <ul class="list-inside list-disc ml-5">
                            <li>your name, telephone and email address for the purposes of sending you direct electronic marketing. Agency Central will only do this if you have given your consent. You may opt-out of receiving this (or specific parts of this) at any time by contacting
                                <a class="text-blue-50"
                                   href="mailto:customersupport@agencycentral.co.uk">customersupport@agencycentral.co.uk</a>
                            </li>
                            <li>to carry out Agency Central’s obligations arising from any contracts entered into between Agency Central and you, for example to take payment, or to allow you to contact agencies, employers and candidates, or to list you on our website as an agency. This is necessary for the performance of Agency Central’s contract with you;</li>
                            <li>to inform agencies about interest in their agency. This is necessary in Agency Central’s legitimate interests to ensure that agencies are fully aware of the value provided by the service, and of the details of individuals who have expressed interest in contacting them;</li>
                            <li>
                                to send you confirmation emails or text messages to verify and confirm your contact details or other information, and to provide you with service updates. This is:
                                <ul class="list-inside list-disc ml-5">
                                    <li>necessary for the performance of Agency Central’s contract with you;</li>
                                    <li>necessary in Agency Central’s legitimate interests to ensure that you are kept fully up to date with the service;</li>
                                </ul>
                            </li>
                            <li>to allow candidates and employers to contact agencies. This is necessary in Agency Central’s legitimate interests to allow the website to function fully, this legitimacy is based on the benefits gained by the agency and the minimal negative impact on them;</li>
                            <li>to contact you about any queries you have, whether via our enquiry page, email, chat or any other method. This is necessary in Agency Central’s legitimate interests to ensure that customer queries, advertising queries and sales queries, are dealt with and responded to;</li>
                            <li>to enhance your experience of the Agency Central website and services, it does this as it is in its legitimate interest to ensure that you receive the best experience possible;</li>
                            <li>to optimise your experience of the types of direct electronic marketing that Agency Central sends you (where you have consented to it). Agency Central does this on the basis that it is necessary in Agency Central’s legitimate interest to ensure that such marketing is of interest and use to you; and</li>
                            <li>to detect and prevent fraud and unauthorised access to Agency Central’s systems. Agency Central does this on the basis that it is necessary in Agency Central’s legitimate interest to do so;</li>
                            <li>any other purposes where Agency Central has obtained your consent to do so.</li>
                        </ul>
                        <p class="pt-2 pb-2">
                            Agency Central may use your information in an anonymised and/or aggregated manner in order to produce management reports and to analyse the data so that it can enhance its IT systems or website. It may share this anonymised and/or aggregated data with third parties, but you will not be identifiable from any such data.
                        </p>
                        <p class="pt-2 pb-2">
                            Agency Central may review your personal data if it has reason to believe that the information has been provided inappropriately, unlawfully or illegally. Agency Central does this on the basis that it is necessary in its legitimate interest to ensure that the website is being used appropriately, lawfully and legally.
                        </p>
                    </div>
                </li>

                <li class="text-lg text-grey-70 font-weight-bold">
                    <div id="disclosing-information-to-third-parties">
                        <span class="font-bold">Disclosing information to third parties</span>
                        <p class="pt-2 pb-2">
                            Agency Central does not sell, trade, or otherwise transfer your personal identifiable information to outside parties other than as set out in this privacy notice.
                        </p>
                        <p class="pt-2 pb-2">
                            Agency Central provides your personal data:
                        </p>
                        <ul class="list-inside list-disc ml-5">
                            <li>to selected third party providers in order to provide certain services to you and use your personal data in accordance with this privacy policy, for example the chat service provider, the CRM system provider, Google Analytics, the social recruiter leaderboard provider, and third parties who help monitor use of the website:</li>
                            <li>to selected marketing agencies, online agencies, development companies, contractors and hosting companies; and</li>
                            <li>to other companies in the same group of companies as Agency Central. These companies will only use your personal data in the same way as Agency Central can under this privacy policy.</li>
                        </ul>
                        <p class="pt-2 pb-2">
                            Agency Central doesn’t allow third-parties to use your personal data for their own purposes and only permits them to process your personal data for specified purposes and in accordance with its instructions.
                        </p>

                        <p class="pt-2 pb-2">
                            Agency Central will only transfer your personal data outside of the EEA to organisations who have adequate safeguards in place to ensure the security of your personal data, we will do so by ensuring that at least one of the following safeguards is implemented:
                        </p>

                        <ul class="list-inside list-disc ml-5">
                            <li>we will only transfer your personal data to countries that have been deemed to provide an adequate level of protection for personal data by the European Commission;</li>
                            <li>where we use certain service providers, we may use specific contracts approved by the European Commission which give personal data the same protection it has in Europe; or</li>
                            <li>where we use providers based in the US, we may transfer data to them if they are part of the Privacy Shield.</li>
                        </ul>

                        <p class="pt-2 pb-2">
                            Agency Central will disclose your personal data to third parties:
                        </p>

                        <ul class="list-inside list-disc ml-5">
                            <li>if Agency Central sells or buys any business or assets, in which case Agency Central will disclose your personal data to the prospective seller or buyer of such business or asset;</li>
                            <li>if Agency Central or substantially all of its assets are acquired by a third party, in which case personal data held by it about its customers will be one of the transferred assets; or</li>
                            <li>if Agency Central is under a duty to disclose or share your personal data in order to comply with any legal obligation, or to protect the rights, property, or safety of Agency Central, you or any third party, and to investigate illegal activities and breaches of any agreement Agency Central has with you. This includes exchanging information with other companies and organisations for the purposes of fraud protection and credit risk reduction.</li>
                        </ul>

                        <p class="pt-2 pb-2">
                            Agency Central may pass your personal data to third parties in a statistical and/or aggregated manner in order to analyse the data.
                        </p>
                        <p>Agency Central will always ensure that you are not identifiable from any such data.</p>
                        <p class="pt-2 pb-2">Agency Central may otherwise share your information as directed by you or subject to your consent.</p>
                    </div>
                </li>

                <li class="text-lg text-grey-70 font-weight-bold">
                    <div id="protecting-your-personal-data<">
                        <span class="font-bold">Protecting your personal data</span>
                        <p class="pt-2 pb-2">
                            Agency Central has put in place appropriate security measures to prevent your personal data from being accidentally lost, used or accessed in an unauthorised way, altered or disclosed. In addition, it limits access to your personal data to those employees, agents, contractors and other third parties who have a business need to know and in accordance with this privacy notice. They will only process your personal data in accordance with Agency Central’s instructions.
                        </p>

                        <p class="pt-2 pb-2">
                            Agency Central has put in place procedures to deal with any suspected personal data breach and will notify you and any applicable regulator of a breach where it is legally required to do so.
                        </p>

                        <p class="pt-2 pb-2">
                            You play an important role in keeping your information secure. If you have any reason to believe that your interactions are no longer secure, then please contact
                            <a class="text-blue-50"
                               href="mailto:customersupport@agencycentral.co.uk">customersupport@agencycentral.co.uk</a>.
                        </p>
                    </div>
                </li>

                <li class="text-lg text-grey-70 font-weight-bold">
                    <div id="holding-your-personal-data">
                        <span class="font-bold">Holding your personal data</span>
                        <p class="pt-2 pb-2">
                            Agency Central will retain your data in accordance with its data retention policy. It may also retain your data for further periods where it needs to do so in order comply with any legal or regulatory requirements (including keeping certain records for VAT purposes), to carry out audits, to detect and prevent fraud, to enforce contracts or to resolve any disputes.
                        </p>
                        <p class="pt-2 pb-2">
                            To determine the appropriate retention period for personal data, Agency Central considers the amount, nature, and sensitivity of the personal data, the potential risk of harm from unauthorised use or disclosure of your personal data, the purposes for which Agency Central processes your personal data and whether those purposes can be achieved through other means, and the applicable legal requirements.
                        </p>
                    </div>
                </li>

                <li class="text-lg text-grey-70 font-weight-bold">
                    <div id="your-rights">
                        <span class="font-bold">Your Rights</span>
                        <p class="pt-2 pb-2">
                            Where Agency Central relies on consent as the condition for processing your personal data, for example for sending you electronic direct marketing, you have the right to withdraw that consent at any time.
                        </p>
                        <p class="pt-2 pb-2">You have various statutory rights in relation to your personal data. In particular, you have the right to:</p>

                        <ul class="list-inside list-disc ml-5">
                            <li>object to direct marketing communications at any time;</li>
                            <li>request and obtain access to your personal data;</li>
                            <li>rectification or erasure of your personal data, in certain circumstances;</li>
                            <li>object to or restrict the processing of your personal data in certain circumstances;</li>
                            <li>have your personal data stored in a manner in which it is portable from the environment in which it is stored by Agency Central to another environment; and</li>
                            <li>file a complaint with the Information Commissioner’s Office.</li>
                        </ul>
                    </div>
                </li>

                <li class="text-lg text-grey-70 font-weight-bold">
                    <div id="cookies">
                        <span class="font-bold">Cookies</span>
                        <p class="pt-2 pb-2">
                            You can set your browser to refuse all or some browser cookies, or to alert you when websites set or access cookies. If you disable or refuse cookies, please note that some parts of this website may become inaccessible or not function properly.
                        </p>

                        <p class="pt-2 pb-2">
                            A cookie is a small information file that is sent to your computer, mobile or other device when you visit a website and it will recognise your device on future visits. These types of files do a number of different jobs, such as remembering your preferences and chosen items, improving your site experience and ensuring that the adverts or offers you see online are more relevant to you. These “cookies” can be divided into 4 types each of which is outlined below.
                        </p>

                        <h3 class="font-bold">Category 1: Strictly Necessary Cookies</h3>
                        <p class="pt-2 pb-2">
                            These cookies are essential for the operation of the Agency Central website in order to enable it to function properly, such as remembering your details and allowing you to access secure or exclusive areas of the website.
                        </p>

                        <h3 class="font-bold">Category 2: Performance Cookies</h3>
                        <p class="pt-2 pb-2">
                            This type collects anonymous information on how people use the site and the data is merged with other users to enable Agency Central to improve how the site operates. They allow Agency Central to recognise and count the number of visitors and to see how visitors move around our website when they are using it.
                        </p>
                        <p class="pt-2 pb-2">
                            For example, the website utilises Google Analytics cookies to help Agency Central understand how customers arrive at the website, how they browse or use the website and highlight areas for improvement such as navigation, shopping experience and marketing campaigns. The data stored by these cookies never shows personal details from which your individual identity can be established.
                        </p>
                        <p class="pt-2 pb-2">
                            These cookies help Agency Central to improve the way its website works, for example, by ensuring that users are finding what they are looking for easily.
                        </p>

                        <h3 class="font-bold">Category 3: Functionality Cookies</h3>
                        <p class="pt-2 pb-2">
                            These are used to recognise you when you return to the website and to personalise content and remember your preferences. These can then be used to provide you with an experience more appropriate with your selections and make the visits more tailored and pleasant.
                        </p>

                        <h3 class="font-bold">Category 4: Targeting Cookies or Advertising Cookies</h3>
                        <p class="pt-2 pb-2">
                            These cookies collect information about your browsing habits in order to make advertising relevant to you and your interests. They remember the pages you have visited and the links you have clicked on. This is used to make the website and adverts more relevant to your interests. This information is shared with other parties such as advertisers.
                        </p>
                    </div>
                </li>

                <li class="text-lg text-grey-70 font-weight-bold">
                    <div id="changes-to-privacy-notice">
                        <span class="font-bold">Changes to Privacy Notice</span>
                        <p class="pt-2 pb-2">
                            If Agency Central decides to change this privacy notice, any changes will be posted on this page.
                        </p>
                        <p class="pt-2 pb-2">
                            This privacy notice was last modified on 24th July 2019.
                        </p>
                    </div>
                </li>


                <li class="text-lg text-grey-70 font-weight-bold">
                    <div id="contact">
                        <span class="font-bold">Contact</span>
                        <p class="pt-2 pb-2">
                            If you wish to contact Agency Central in relation to this privacy notice, including in relation to any of the rights set out in section 7 above, please contact
                            <a class="text-blue-50"
                               href="mailto:customersupport@agencycentral.co.uk">customersupport@agencycentral.co.uk</a>.
                        </p>
                    </div>
                </li>
            </ol>
        </div>
    </layout-page>
</template>

<script>
import LayoutPage from 'Components/layouts/LayoutPage';
import NavBar from 'Components/NavBar/NavBar';
import PageBreadcrumb from 'Components/PageBreadcrumb';

export default {
    name: 'PrivacyPolicy',
    components: {
        PageBreadcrumb,
        LayoutPage,
        NavBar,
    },
    computed: {
      breadCrumbs : function(){
        return [
          {
            name: 'Privacy Policy'
          }
        ]
      }
    }
};
</script>
